export const originalLanguage = {
  value: '', // empty string means original language
  flag: 'globe',
  label: 'Original',
} as const;

export const clientLanguages = [
  { value: 'de', flag: 'de', label: 'Deutsch' },
  { value: 'en', flag: 'gb', label: 'English' },
  { value: 'fr-CH', flag: 'fr', label: 'Français' },
  { value: 'it-CH', flag: 'it', label: 'Italiano' },
  { value: 'pl', flag: 'pl', label: 'Polski' }, // polish
  { value: 'cz', flag: 'cz', label: 'Česky' }, // czech
  { value: 'sk', flag: 'sk', label: 'Slovenský' }, // slovakian
  { value: 'ru', flag: 'ru', label: 'Русский' }, // russian
  { value: 'hu', flag: 'hu', label: 'Magyar' }, // hungarian
  { value: 'ro', flag: 'ro', label: 'Română' }, // romanian
  { value: 'nl', flag: 'nl', label: 'Nederlands' }, // dutch
  { value: 'si', flag: 'si', label: 'Slovenščina' }, // slovenian
  { value: 'bg', flag: 'bg', label: 'български' }, // Bulgarian
  { value: 'ar', flag: 'sa', label: 'العربية' }, // Arabic
  { value: 'es', flag: 'es', label: 'Español' }, // Spanish
  { value: 'et', flag: 'ee', label: 'Eesti' }, // Estonian
  { value: 'zh', flag: 'cn', label: '中文' }, // Chinese
  { value: 'id', flag: 'id', label: 'Bahasa Indonesia' }, // Indonesian
  { value: 'uk', flag: 'ua', label: 'Українська' }, // Ukrainian
  { value: 'tr', flag: 'tr', label: 'Türkçe' }, // Turkish
  { value: 'bs', flag: 'ba', label: 'Bosanski' }, // Bosnian
  { value: 'hr', flag: 'hr', label: 'Hrvatski' }, // Croatian
] as const;

export const supportedLanguages = clientLanguages.map((language) => language.value);

export type LanguageCode = typeof clientLanguages[number]['value'] | '';

export const deeplLanguages = [
  'bg', 'cs',    'da',    'de',
  'el', 'en-GB', 'en-US', 'es',
  'et', 'fi',    'fr',    'hu',
  'id', 'it',    'ja',    'ko',
  'lt', 'lv',    'nb',    'nl',
  'pl', 'pt-BR', 'pt-PT', 'ro',
  'ru', 'sk',    'sl',    'sv',
  'tr', 'uk',    'zh',    'zh-HANS',
  'hr', 'bs',
];

/**
 * Maps our language codes to deepl language codes.
 */
export const mapLanguageCodeToDeepl = (lng: LanguageCode) => {
  switch (lng) {
    case 'cz':
      return 'cs';
    case 'en':
      return 'en-US';
    case 'fr-CH':
      return 'fr';
    case 'it-CH':
      return 'it';
    case 'si':
      return 'sl';
    default:
      return lng;
  }
};

/**
 * Maps our language codes to deepl SOURCE language codes.
 * DeepL only accepts regional variants (en-US, pt-BR, ...) as target languages - source_lang must be
 * the base code (https://developers.deepl.com/docs/getting-started/supported-languages#source-languages),
 * so the target mapping's regional picks are reduced to their base.
 */
export const mapLanguageCodeToDeeplSource = (lng: LanguageCode) =>
  mapLanguageCodeToDeepl(lng).replace(/-.*$/, '');

export const mapDeeplToLanguageCode = (deeplLng: string): LanguageCode => {
  switch (deeplLng) {
    case 'cs':
      return 'cz';
    case 'en-US':
    case 'en-GB':
      return 'en';
    case 'fr':
      return 'fr-CH';
    case 'it':
      return 'it-CH';
    case 'sl':
      return 'si';
    default:
      return deeplLng as LanguageCode;
  }
};

/**
 * Maps a DeepL-detected source language to our language code, or null when the detection
 * falls outside our client languages (DeepL also detects e.g. Norwegian or Japanese).
 * Callers must treat such units as undetected - there is nothing we could label, offer, or set.
 */
export const mapDetectedLanguageCode = (deeplLng: string): LanguageCode | null => {
  const mapped = mapDeeplToLanguageCode(deeplLng);
  return (supportedLanguages as string[]).includes(mapped) ? mapped : null;
};

// used to set the language for AI requests
export const clientLanguagesEnglish = [
  { value: 'original', label: 'Original' },
  { value: 'de', label: 'German' },
  { value: 'en', label: 'English' },
  { value: 'fr-CH', label: 'French' },
  { value: 'it-CH', label: 'Italian' },
  { value: 'pl', label: 'Polish' },
  { value: 'cz', label: 'Czech' },
  { value: 'sk', label: 'Slovak' },
  { value: 'ru', label: 'Russian' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'ro', label: 'Romanian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'si', label: 'Slovenian' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'es', label: 'Spanish' },
  { value: 'et', label: 'Estonian' },
  { value: 'zh', label: 'Chinese' },
  { value: 'id', label: 'Indonesian' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'tr', label: 'Turkish' },
  { value: 'hr', label: 'Croatian' },
  { value: 'bs', label: 'Bosnian' },
] as const;

export const getLanguageLabel = (value: LanguageCode): string => {
  if (value === '') return originalLanguage.label;

  const language = clientLanguages.find((language) => language.value === value);
  return language?.label || '';
};

export const getEnglishLanguageLabel = (value?: LanguageCode): string => {
  const language = clientLanguagesEnglish.find((language) => language.value === value);
  return language?.label || '';
};

/**
 * Client Only!
 *
 * Used to set the content language for all requests.
 */
export const setContentLanguage = (language?: LanguageCode) => {
  if (typeof window !== 'undefined') {
    if (language && supportedLanguages.includes(language)) {
      window.localStorage.setItem('language', language);
    } else {
      window.localStorage.removeItem('language');
    }
  }
};

export const getContentLanguage = (): LanguageCode => {
  if (typeof window !== 'undefined') {
    const lng = window.localStorage.getItem('language') as LanguageCode;

    if (!lng || !supportedLanguages.includes(lng)) return '';

    return lng;
  }

  return '';
};

export const getLanguageHeader = (lng?: LanguageCode) => lng ? { 'x-language': lng } : undefined;

const isoToInternalCodeMap: Record<string, string> = {
  cs: 'cz',
  sl: 'si',
};

/**
 * Build a map of base languages to their regional variants we support.
 * e.g., { fr: 'fr-CH', it: 'it-CH' }
 */
const regionalVariantMap: Record<string, string> = clientLanguages
  .filter(lang => lang.value.includes('-'))
  .reduce((acc, lang) => {
    const baseLang = lang.value.split('-')[0];
    acc[baseLang] = lang.value;
    return acc;
  }, {} as Record<string, string>);

/**
 * Normalize a detected language code to match our supported languages.
 *
 * Handles:
 * - Regional variants: 'de-AT' → 'de', 'en-US' → 'en', 'en-GB' → 'en'
 * - Regional mappings: 'fr' or 'fr-FR' → 'fr-CH', 'it' or 'it-IT' → 'it-CH'
 * - ISO code mismatches: 'cs' → 'cz' (Czech), 'sl' → 'si' (Slovenian)
 *
 * @param lng - The detected language code (e.g., 'de-AT', 'fr-FR', 'cs')
 * @returns The normalized language code that matches our supportedLanguages
 */
export const normalizeLanguageCode = (lng: string): string => {
  if (!lng) return lng;

  const lngLower = lng.toLowerCase();

  // if exact match - return as-is
  if ((supportedLanguages as readonly string[]).includes(lngLower)) {
    return lngLower;
  }

  // extract base language (de-AT → de, fr-FR → fr)
  const baseLang = lngLower.split('-')[0];

  // check ISO code mapping (cs → cz, sl → si)
  const isoMapped = isoToInternalCodeMap[baseLang];
  if (isoMapped && (supportedLanguages as readonly string[]).includes(isoMapped)) {
    return isoMapped;
  }

  // check if we have a regional variant for this base language (fr → fr-CH)
  const regionalVariant = regionalVariantMap[baseLang];
  if (regionalVariant && (supportedLanguages as readonly string[]).includes(regionalVariant)) {
    return regionalVariant;
  }

  // check if base language is directly supported (de, en, pl, etc.)
  if ((supportedLanguages as readonly string[]).includes(baseLang)) {
    return baseLang;
  }

  // if no match found - return original (i18n will use fallbackLng)
  return lng;
};
